/**
 * Boot file for the builder editor plugin registry.
 *
 * Importing this file once at app boot registers all built-in editors. Add
 * your project-specific editors by calling `registerEditor(type, plugin)`
 * AFTER importing this file (e.g. in your app root or a startup module).
 *
 * See ./registry.js for the plugin shape.
 */
import { registerEditor } from './registry'

import { TextEditor }         from './TextEditor'
import { TextareaEditor }     from './TextareaEditor'
import { RichTextEditor, RichTextCell } from './RichTextEditor'
import { IconEditor }         from './IconEditor'
import { MediaEditor, MediaUrlEditor } from './MediaEditor'
import { LinkEditor }         from './LinkEditor'
import { NodeRefEditor }      from './NodeRefEditor'
import { LensVisualizationEditor } from './LensVisualizationEditor'
import { TermRefCell }        from './TermRefEditor'
import { ObjectRefEditor }    from './ObjectRefEditor'
import { ObjectRefCell }      from './ObjectRefCell'
import { FragmentRefEditor }  from './FragmentRefEditor'
import { SharedUnitRefEditor } from './SharedUnitRefEditor'
import { SelectEditor }       from './SelectEditor'
import { DynamicSelectEditor } from './DynamicSelectEditor'
import { CollectionEditor }   from './CollectionEditor'
import { BackgroundEditor }   from './BackgroundEditor'
import { YoutubeUrlEditor }   from './YoutubeUrlEditor'
import { ImageSizingEditor }  from './ImageSizingEditor'
import { ProcessEditor }      from './ProcessEditor'

import {
  TextCell,
  TextareaCell,
  IconCell,
  ImageDrupalCell,
  LinkCell,
  CollectionCell,
  GenericModalCell,
} from './cells'

// --- inline cell types ------------------------------------------------------

registerEditor('text', {
  mode: 'inline',
  Editor: TextEditor,
  Cell: TextCell,
  defaultValue: '',
})

registerEditor('textarea', {
  mode: 'inline',
  Editor: TextareaEditor,
  Cell: TextareaCell,
  defaultValue: '',
})

// L'icône se choisit dans LA bibliothèque plein écran (set Lucide complet,
// rangé par thèmes) : `surface: 'fullscreen'` dit au FieldEditorPopover de la
// rendre nue — elle porte son propre dialogue en portail, comme la médiathèque.
// `mode: 'inline'` reste vrai du CollectionEditor, où la cellule est un bouton
// d'icône qui ouvre cette même bibliothèque.
registerEditor('icon', {
  mode: 'inline',
  surface: 'fullscreen',
  Editor: IconEditor,
  Cell: IconCell,
  defaultValue: 'Star',
})

// --- modal cell types (preview button + sub-modal editor) -------------------

// Les deux champs image ouvrent LA médiathèque (pop-in plein écran). Ils ne
// diffèrent que par ce qu'ils stockent : une référence au média (`image-drupal`,
// la forme de référence, recadrable à la volée) ou une URL nue (`image`, forme
// héritée conservée pour les définitions de blocs déjà écrites).
// `surface: 'fullscreen'` dit au FieldEditorPopover de les rendre nus — la
// médiathèque porte son propre dialogue en portail.
registerEditor('image', {
  mode: 'modal',
  surface: 'fullscreen',
  Editor: MediaUrlEditor,
  Cell: ImageDrupalCell,        // image-style preview works for both shapes
  defaultValue: null,
})

registerEditor('image-drupal', {
  mode: 'modal',
  surface: 'fullscreen',
  Editor: MediaEditor,
  Cell: ImageDrupalCell,
  defaultValue: null,
})

registerEditor('link', {
  mode: 'modal',
  Editor: LinkEditor,
  Cell: LinkCell,
  defaultValue: '',
})

// `lens-visualization` — a visualization PUBLISHED from Lens (value `{nid, label}`),
// shown by the « Visualisation » block ; edited in Lens, in a new window.
registerEditor('lens-visualization', {
  mode: 'modal',
  Editor: LensVisualizationEditor,
  Cell: GenericModalCell,
  defaultValue: null,
})

registerEditor('node-ref', {
  mode: 'modal',
  Editor: NodeRefEditor,
  Cell: GenericModalCell,
  defaultValue: null,
})

// `term-ref` — une catégorie en saisie libre, prise dans les taxonomies DU
// SITE. Un champ, pas une pop-in : on tape, on choisit ou on crée, et c'est le
// « Enregistrer » du formulaire qui tranche — d'où `mode: 'inline'`, qui rend
// la cellule comme contrôle de saisie au lieu d'un aperçu à cliquer.
registerEditor('term-ref', {
  mode: 'inline',
  Cell: TermRefCell,
  defaultValue: null,
})

// `object-ref` references one or many builder_unit nodes (object instances) of a
// given type. Supports `multiple: true` + `max: N` via the field config. The
// stored value is the minimum envelope (id, type, label, slug) ; the full
// data is server-resolved at render time by OrbitDataEnricher.
registerEditor('object-ref', {
  mode: 'modal',
  Editor: ObjectRefEditor,
  Cell: ObjectRefCell,
  defaultValue: null,
})

// `fragment-ref` references a reusable builder_fragment to embed (Fragment
// block). Value: {nid, slug, label}. The fragment itself is a self-describing
// editable unit, persisted on its own submission_url.
registerEditor('fragment-ref', {
  mode: 'modal',
  Editor: FragmentRefEditor,
  Cell: GenericModalCell,
  defaultValue: null,
})

// `shared-unit-ref` pointe vers une unité DÉJÀ POSÉE (bloc partagé) : le porteur
// `SharedBlock` ne stocke que `{nid, type, label}`, tout le contenu vit dans
// l'unité cible et reste donc commun à toutes ses places. Même composant que la
// pop-in « Ajouter un bloc existant » — une seule liste, deux surfaces.
registerEditor('shared-unit-ref', {
  mode: 'modal',
  Editor: SharedUnitRefEditor,
  Cell: GenericModalCell,
  defaultValue: null,
})

registerEditor('select', {
  mode: 'modal',
  Editor: SelectEditor,
  Cell: GenericModalCell,
  defaultValue: '',
})

// `es-select` — like `select` but options are fetched from an endpoint at edit
// time (portable block defs; referential filters like pipeline). Lot L3.
registerEditor('es-select', {
  mode: 'modal',
  Editor: DynamicSelectEditor,
  Cell: GenericModalCell,
  defaultValue: '',
})

registerEditor('background', {
  mode: 'modal',
  Editor: BackgroundEditor,
  Cell: GenericModalCell,
  defaultValue: null,
})

registerEditor('youtube-url', {
  mode: 'modal',
  Editor: YoutubeUrlEditor,
  Cell: GenericModalCell,
  defaultValue: '',
})

// `image-sizing` — l'engrenage de la poignée de redimensionnement d'image. Il
// n'est JAMAIS déclaré dans un `editable:` : c'est le canevas qui l'ouvre, sur
// le champ `image-drupal` survolé, pour régler cadrage, format et tailles par
// point de rupture. La valeur qu'il reçoit et renvoie est la valeur d'image
// entière ; il n'en touche que la clé `sizing`.
registerEditor('image-sizing', {
  mode: 'modal',
  Editor: ImageSizingEditor,
  Cell: GenericModalCell,
  defaultValue: null,
})

// `process` — le flux ECA qui traite les saisies d'un formulaire. Il n'édite pas
// un contenu mais un CÂBLAGE : la valeur est l'URL d'endpoint que le `submitData`
// du bloc poste, et le panneau montre qui répond dessus, quels autres
// formulaires s'en servent, et le flux lui-même.
//
// `surface: 'fullscreen'` — un éditeur ECA n'entre pas dans une pop-in ancrée de
// 42 rem ; le panneau porte son propre dialogue en portail, comme la médiathèque.
registerEditor('process', {
  mode: 'modal',
  surface: 'fullscreen',
  Editor: ProcessEditor,
  Cell: GenericModalCell,
  defaultValue: '',
})

// `rich-text` s'édite normalement EN PLACE : TipTap se monte DANS l'élément du
// champ (`activateInlineRichText`), et c'est pourquoi il n'avait pas d'entrée
// ici. Mais toutes les surfaces n'ont pas de rendu où écrire — le panneau
// « Champs du contenu » d'un objet, et son formulaire quand aucun modèle ne
// l'affiche, listent le champ sans rien à cliquer : sans cette entrée, ils
// rendent « Type d'éditeur inconnu : « rich-text » » sur le corps d'un article.
// `RichTextEditor` porte déjà la même barre d'outils que l'édition en place ;
// celle-ci reste la voie normale et ne passe pas par le registre.
registerEditor('rich-text', {
  // `inline` : la cellule EST l'éditeur, posé dans le formulaire. Le corps
  // d'un article est le champ le plus long de la page — le mettre derrière un
  // bouton d'aperçu et une pop-in reviendrait à cacher la matière du contenu.
  // `Editor` reste déclaré : c'est lui que le CollectionEditor ouvre pour une
  // colonne `rich-text` d'une liste, où la place manque pour un éditeur entier.
  mode: 'inline',
  Editor: RichTextEditor,
  Cell: RichTextCell,
  defaultValue: '',
})

// `list` (and its legacy alias `stats-list`) is the recursive collection
// editor — it itself uses getEditor() to resolve each cell type. Registering
// here means a list can contain another list of arbitrary depth.
registerEditor('list', {
  mode: 'modal',
  Editor: CollectionEditor,
  Cell: CollectionCell,
  defaultValue: [],
})

registerEditor('stats-list', {
  mode: 'modal',
  Editor: CollectionEditor,
  Cell: CollectionCell,
  defaultValue: [],
})

// Re-export the registry helpers for convenience.
export { registerEditor, getEditor, hasEditor, listEditors } from './registry'
